import { useState, useCallback } from 'react';
import { DataSource, Folder as FolderType } from '../../../types/files';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';

type SourceType = 'all' | 'file_upload' | 'drive_sync';
type SortField = 'name' | 'size' | 'updated_at' | 'source_type';
type SortDirection = 'asc' | 'desc';

interface FetchParams {
    currentPage: number;
    itemsPerPage: number;
    searchTerm: string;
    source: SourceType;
    sort: {
        field: SortField;
        direction: SortDirection;
    };
    currentFolderId: string | null;
    rootFolderId?: string | null;
}

interface RefreshParams extends FetchParams {
    targetFolderId: string | null;
}

interface FolderResponse {
    folders: Array<FolderType & { parent_id?: string | null }>;
}

interface ListResponse {
    dataSources: DataSource[];
    total: number;
}

interface UploadUrlResponse {
    uploadUrl: string;
    objectName: string;
    dataSourceId: string;
}

interface RootFolderResponse {
    rootFolder?: FolderType;
    folders?: FolderType[];
}

export const useFileOperations = (onError?: (error: string) => void) => {
    const [files, setFiles] = useState<DataSource[]>([]);
    const [folders, setFolders] = useState<FolderType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [uploadStatus, setUploadStatus] = useState({
        isUploading: false,
        totalFiles: 0,
        currentFile: 0,
        currentFileName: ''
    });

    const getFiles = useCallback(async (params: FetchParams) => {
        setIsLoading(true);
        setError(null);
        try {
            const folderId = params.currentFolderId || params.rootFolderId;
            const queryParams = new URLSearchParams({
                page: params.currentPage.toString(),
                limit: params.itemsPerPage.toString(),
                search: params.searchTerm,
                source: params.source,
                sort_by: params.sort.field,
                sort_direction: params.sort.direction,
                folder_id: folderId || ''
            });

            const data = await apiService.get<ListResponse>(`${API_ENDPOINTS.DATA_SOURCE.LIST}?${queryParams}`);

            const fileItems = (data.dataSources as DataSource[])
                .filter((item): item is DataSource & { item_type?: 'file' } =>
                    !item.item_type || item.item_type === 'file'
                )
                .map(file => ({
                    ...file,
                    metadata: {
                        ...file.metadata,
                        file_size: file.metadata?.original_metadata?.file_size || file.metadata?.file_size,
                        mime_type: file.metadata?.original_metadata?.mime_type || file.metadata?.mime_type,
                        original_filename: file.metadata?.original_metadata?.original_filename || file.metadata?.original_filename,
                        object_name: file.metadata?.object_name || file.metadata?.original_metadata?.object_name
                    }
                }));

            setFiles(fileItems);
            setIsInitialLoad(false);

            return {
                total: data.total,
                totalPages: Math.ceil(data.total / params.itemsPerPage)
            };
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data sources';
            setError(errorMessage);
            onError?.(errorMessage);
            return { total: 0, totalPages: 0 };
        } finally {
            setIsLoading(false);
            setIsInitialLoad(false);
        }
    }, [onError]);

    const fetchFolderHierarchy = useCallback(async (): Promise<FolderResponse> => {
        return apiService.get<FolderResponse>(API_ENDPOINTS.DATA_SOURCE.LIST_FOLDERS_HIERARCHY);
    }, []);

    const fetchFolders = useCallback(async (currentFolderId: string | null) => {
        try {
            const { folders: allFolders = [] } = await fetchFolderHierarchy();

            const isValidFolder = (folder: FolderType & { parent_id?: string | null }, currentParentId: string | null): boolean => {
                if (!folder.id) return false;
                if (folder.id === folder.parent_id) return false;
                if (folder.id === currentParentId) return false;

                if (currentParentId) {
                    return folder.parent_id === currentParentId;
                }

                return folder.parent_id === null || Boolean(folder.is_root);
            };

            const validFolders = allFolders
                .filter(folder => isValidFolder(folder, currentFolderId))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

            setFolders(validFolders);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch folders';
            setError(errorMessage);
            onError?.(errorMessage);
        }
    }, [fetchFolderHierarchy, onError]);

    const fetchRootFolder = useCallback(async () => {
        try {
            const data = await apiService.get<RootFolderResponse>(
                `${API_ENDPOINTS.DATA_SOURCE.LIST_FOLDERS}?root=true`
            );

            if (data.rootFolder?.id) {
                return data.rootFolder.id;
            }

            const rootFolder = data.folders?.find((f: FolderType) => f.is_root && f.name.toLowerCase() === 'home');
            return rootFolder?.id || null;
        } catch (err) {
            return null;
        }
    }, []);

    const refreshAllFolders = useCallback(async (params: RefreshParams) => {
        try {
            const { folders: allFolders = [] } = await fetchFolderHierarchy();

            const foldersToRefresh = new Set<string>();

            if (params.currentFolderId) {
                foldersToRefresh.add(params.currentFolderId);
            }

            if (params.targetFolderId) {
                foldersToRefresh.add(params.targetFolderId);
            }

            allFolders.forEach(folder => {
                if (folder.parent_id) {
                    foldersToRefresh.add(folder.parent_id);
                }
            });

            for (const folderId of foldersToRefresh) {
                await fetchFolders(folderId);
            }

            return getFiles(params);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to refresh folders';
            setError(errorMessage);
            onError?.(errorMessage);
        }
    }, [fetchFolderHierarchy, fetchFolders, getFiles, onError]);

    const handleUpload = useCallback(async (files: File[], folderId: string | null) => {
        if (files.length === 0) return;

        setUploadStatus({
            isUploading: true,
            totalFiles: files.length,
            currentFile: 0,
            currentFileName: files[0].name
        });

        try {
            for (const [index, file] of files.entries()) {
                setUploadStatus(prev => ({
                    ...prev,
                    currentFile: index + 1,
                    currentFileName: file.name
                }));

                // Get upload URL
                const { uploadUrl, objectName, dataSourceId } = await apiService.get<UploadUrlResponse>(
                    `${API_ENDPOINTS.FILES.UPLOAD_URL}?filename=${encodeURIComponent(file.name)}`
                );

                // Upload file
                const uploadResponse = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': file.type,
                        'Content-Length': file.size.toString()
                    },
                    body: file
                });

                if (!uploadResponse.ok) {
                    throw new Error(`Upload failed for ${file.name}`);
                }

                // Complete upload
                await apiService.post(API_ENDPOINTS.FILES.UPLOAD_COMPLETE, {
                    dataSourceId,
                    objectName,
                    size: file.size,
                    mimeType: file.type,
                    folderId
                });
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to upload files';
            setError(errorMessage);
            onError?.(errorMessage);
        } finally {
            setUploadStatus({
                isUploading: false,
                totalFiles: 0,
                currentFile: 0,
                currentFileName: ''
            });
        }
    }, [onError]);

    const deleteFile = useCallback(async (fileId: string): Promise<boolean> => {
        try {
            await apiService.delete(`${API_ENDPOINTS.DATA_SOURCE.DELETE}/${fileId}`);
            return true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete file';
            onError?.(errorMessage);
            return false;
        }
    }, [onError]);

    const deleteFolder = useCallback(async (folderId: string): Promise<{ success: boolean; message: string }> => {
        try {
            await apiService.delete(`${API_ENDPOINTS.DATA_SOURCE.DELETE_FOLDER}/${folderId}`);
            return {
                success: true,
                message: 'Folder deleted successfully'
            };
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete folder';
            onError?.(errorMessage);
            return {
                success: false,
                message: errorMessage
            };
        }
    }, [onError]);

    const handleDeleteItem = useCallback(async (itemId: string, itemType: 'folder' | 'file'): Promise<{ success: boolean; message: string }> => {
        try {
            if (itemType === 'folder') {
                return deleteFolder(itemId);
            } else {
                const success = await deleteFile(itemId);
                return {
                    success,
                    message: success ? 'File deleted successfully' : 'Failed to delete file'
                };
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete item';
            return {
                success: false,
                message: errorMessage
            };
        }
    }, [deleteFile, deleteFolder]);

    const moveFiles = useCallback(async (
        fileIds: string[],
        targetFolderId: string | null,
        params: {
            rootFolderId: string | null;
            currentFolderId: string | null;
            currentPage: number;
            itemsPerPage: number;
            searchTerm: string;
            source: SourceType;
            sort: {
                field: SortField;
                direction: SortDirection;
            };
        }
    ): Promise<void> => {
        setIsLoading(true);
        try {
            const actualTargetFolderId = targetFolderId === null ? params.rootFolderId : targetFolderId;

            // Refresh before moving
            await Promise.all([
                getFiles(params),
                fetchFolders(params.currentFolderId)
            ]);

            const { folders: currentFolders = [] } = await fetchFolderHierarchy();
            const folderId = params.currentFolderId || params.rootFolderId;
            const queryParams = new URLSearchParams({
                page: params.currentPage.toString(),
                limit: params.itemsPerPage.toString(),
                search: params.searchTerm,
                source: params.source,
                sort_by: params.sort.field,
                sort_direction: params.sort.direction,
                folder_id: folderId || ''
            });

            const filesData = await apiService.get<ListResponse>(`${API_ENDPOINTS.DATA_SOURCE.LIST}?${queryParams}`);
            const currentFiles = filesData.dataSources || [];

            for (const itemId of fileIds) {
                const fileItem = currentFiles.find((f: { id: string; }) => f.id === itemId);
                const folderItem = currentFolders.find(f => f.id === itemId);
                const item = fileItem || folderItem;
                const isFolder = Boolean(folderItem);

                if (!item) {
                    throw new Error(`Item ${itemId} not found in the current list. Please refresh and try again.`);
                }

                const endpoint = isFolder
                    ? API_ENDPOINTS.DATA_SOURCE.MOVE_FOLDER
                    : API_ENDPOINTS.DATA_SOURCE.MOVE_TO_FOLDER;

                await apiService.post(endpoint, {
                    sourceId: itemId,
                    folderId: actualTargetFolderId
                });
            }

            await refreshAllFolders({
                ...params,
                targetFolderId: actualTargetFolderId
            });

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to move files';
            setError(errorMessage);
            onError?.(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [refreshAllFolders, onError, fetchFolderHierarchy]);

    const deleteFiles = useCallback(async (itemIds: string[]): Promise<boolean> => {
        try {
            const allItems = [...files, ...folders];
            const results = await Promise.all(itemIds.map(async (itemId) => {
                const item = allItems.find(item => item.id === itemId);
                if (!item) return false;

                const itemType = 'is_root' in item ? 'folder' : 'file';
                const result = await handleDeleteItem(itemId, itemType);
                return result.success;
            }));

            return results.every(result => result === true);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete items';
            setError(errorMessage);
            onError?.(errorMessage);
            return false;
        }
    }, [handleDeleteItem, onError, files, folders]);

    return {
        files,
        folders,
        isLoading,
        isInitialLoad,
        error,
        uploadStatus,
        fetchFiles: getFiles,
        fetchFolders,
        fetchRootFolder,
        handleUpload,
        deleteFile,
        deleteFiles,
        moveFiles,
        refreshAllFolders,
        deleteFolder,
        handleDeleteItem
    };
};